import { parseWorkflowViewState } from '@aline/common';
import type { WorkflowViewState } from '@aline/common';
import { Apollo } from 'src/gql/generated';

import {
  getAppliedWorkflowListStateFromWorkflowView,
  getWorkflowViewStateFromWorkflowList,
  WORKFLOW_LIST_CHART_ID,
} from '../workflow/workflow-list/workflow-list-charts';
import type {
  WorkflowListChartConfig,
  WorkflowListChartsIntegrationSlice,
} from '../workflow/workflow-list/workflow-list-charts';

export const INSIGHT_VIEW_EDIT_DISABLED_REASON = 'Only admins or the view owner can edit';

export function getCanEditInsightView(options: {
  isOrgAdmin: boolean;
  authorId?: string;
  userId?: string;
}): boolean {
  const { authorId, isOrgAdmin, userId } = options;
  return Boolean(userId && authorId && (authorId === userId || isOrgAdmin));
}

export function getChartPinScope(
  pins: readonly { chartId: string; scope: Apollo.InsightScope }[],
  chartId: string
): Apollo.InsightScope {
  return pins.find(pin => pin.chartId === chartId)?.scope ?? Apollo.InsightScope.Personal;
}

export function getMutationFailureMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

export function getBaselineBuilderState(
  viewState: unknown,
  requestedChartId: string | null
): { activeChartId: string; state: WorkflowViewState } {
  const applied = getAppliedWorkflowListStateFromWorkflowView(viewState);
  const nextActiveChartId =
    requestedChartId && applied.chartsIntegration.charts?.[requestedChartId]
      ? requestedChartId
      : (applied.chartsIntegration.activeChartId ?? WORKFLOW_LIST_CHART_ID);

  return {
    activeChartId: nextActiveChartId,
    state: getWorkflowViewStateFromWorkflowList({
      aggregationModel: applied.aggregationModel,
      chartsIntegration: {
        ...applied.chartsIntegration,
        activeChartId: nextActiveChartId,
      },
      filterModel: applied.filterModel,
      rowGroupingModel: applied.rowGroupingModel,
      sortModel: applied.sortModel,
      workflowFilter: applied.workflowFilter,
    }),
  };
}

export function getChartsWithNewChart(
  slice: WorkflowListChartsIntegrationSlice,
  chartId: string,
  chart: WorkflowListChartConfig
): WorkflowListChartsIntegrationSlice {
  return {
    activeChartId: chartId,
    charts: {
      ...slice.charts,
      [chartId]: chart,
    },
  };
}

export function getChartsWithoutChart(
  slice: WorkflowListChartsIntegrationSlice,
  chartId: string
): WorkflowListChartsIntegrationSlice {
  const charts = Object.fromEntries(
    Object.entries(slice.charts ?? {}).filter(([candidateId]) => candidateId !== chartId)
  );
  const activeChartId =
    slice.activeChartId === chartId ? Object.keys(charts).at(0) : slice.activeChartId;
  return { ...slice, activeChartId, charts };
}

export function getChartsWithTitle(
  slice: WorkflowListChartsIntegrationSlice,
  chartId: string,
  title: string | undefined
): WorkflowListChartsIntegrationSlice {
  const chart = slice.charts?.[chartId];
  if (!chart) return slice;
  return {
    ...slice,
    charts: { ...slice.charts, [chartId]: { ...chart, title } },
  };
}

export function getStateWithCharts(
  state: WorkflowViewState,
  chartsIntegration: WorkflowListChartsIntegrationSlice
): WorkflowViewState {
  return parseWorkflowViewState({ ...state, chartsIntegration });
}
